import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/free-solid-svg-icons";

export const PhaseManage = () => {
  const { staff } = useStaffAuthContext();

  const [phases, setPhases] = useState([]);
  const [filteredPhases, setFilteredPhases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchPhases = async (page) => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(
        `${API_URL.GET_PHASE_ALL}?page=${page}&limit=50`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        }
      );
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to fetch phases");
      }

      setPhases(data.phases);
      setFilteredPhases(data.phases);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const deletePhase = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL.DELETE_PHASE}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to delete phase");
      }

      // Refresh the phases list
      fetchPhases(currentPage);
      alert("Phase deleted successfully!");
    } catch (err) {
      alert(`Error deleting phase: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this phase?")) {
      deletePhase(id);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    filterPhases(query);
  };

  const filterPhases = (query) => {
    if (query.trim() === "") {
      setFilteredPhases(phases);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = phases.filter((phase) =>
      phase.title.toLowerCase().includes(lowerQuery)
    );

    setFilteredPhases(filtered);
  };

  useEffect(() => {
    fetchPhases(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">
        <div className="flex justify-end gap-4 mb-4">
          <Link
            to="/administrator/warmup-routine/reorder-phase"
            className="inline-flex items-center px-4 py-2 bg-green-500 text-white font-semibold rounded-md shadow hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <FontAwesomeIcon icon={faSort} className="mr-2" />
            Reorder Phases
          </Link>

          <Link
            to="/administrator/warmup-routine/create-phase"
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white font-semibold rounded-md shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Create Phase
          </Link>
        </div>

        <h1 className="text-2xl font-bold mb-4">Manage Phases</h1>

        <div className="flex justify-end gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by title"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-1/4 p-2 border border-gray-300 rounded"
          />
        </div>

        <div className="flex-1 overflow-auto border rounded shadow bg-white">
          <table className="table-auto w-full">
            <thead className="sticky top-0 bg-gray-200">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Phase Actions</th>
                <th className="px-4 py-2 text-left">Exercise Actions</th> {/* New column header */}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="2" className="text-center py-4">
                    <p className="text-blue-600">Loading data...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="2" className="text-center py-4">
                    <p className="text-red-600">{error}</p>
                  </td>
                </tr>
              ) : filteredPhases.length === 0 ? (
                <tr>
                  <td colSpan="2" className="text-center py-4">
                    <p>No records found</p>
                  </td>
                </tr>
              ) : (
                filteredPhases.map((phase, index) => (
                  <tr
                    key={phase._id}
                    className={`hover:bg-gray-100 border-b border-gray-200 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                  >
                    <td className="px-4 py-2">{phase.title}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleDelete(phase._id)}
                          className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                          disabled={isDeleting}
                        >
                          Delete
                        </button>
                        <Link
                          to={`/administrator/warmup-routine/edit-phase/${phase._id}`}
                          className="px-3 py-1 text-xs bg-blue-500 text-white text-center rounded hover:bg-blue-600"
                        >
                          Edit
                        </Link>
                      
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/administrator/warmup-routine/create-phase-exercise/${phase._id}`}
                          className="px-3 py-1 text-xs bg-green-500 text-white text-center rounded hover:bg-green-600"
                        >
                          Add Exercise
                        </Link>
                        <Link
                          to={`/administrator/warmup-routine/manage-phase-exercise/${phase._id}`}
                          className="px-3 py-1 text-xs bg-purple-500 text-white text-center rounded hover:bg-purple-600"
                        >
                          Manage Exercises
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded ${
              currentPage === 1
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded ${
              currentPage === totalPages
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
